/**
 * Unified API client with JWT authentication.
 * Token persisted in sessionStorage (survives refresh, clears on tab close).
 * Auto-refreshes token 5 minutes before expiry.
 */

import { computeFileMD5 } from '../utils/md5'
import type {
  Asset,
  AssetStats,
  StorageQuota,
  StorageInfo,
  StorageTierStats,
  SearchResponse,
  Tag,
  BMIResult,
  VaultStats,
  VaultItem,
  User,
  TeamMember,
  TeamUsage,
  ShareLink,
  BrandProfile,
  BrandProfileStats,
  Task,
  TwoFAStatus,
  TwoFASetup,
  AuditLog,
  OnboardingStatus,
  InvitationCode,
  DedupReport,
  SavingsReport,
  PlansResponse,
  CurrentPlan,
  SimilarSearchResult,
  UploadUrlResponse,
  DedupCheckResponse,
  KnowledgeFacts,
} from '../types'

const BASE_URL = '/api/v1'
const TOKEN_KEY = 'omnituring_token'

let _token: string | null = sessionStorage.getItem(TOKEN_KEY) || null
let _onUnauthorized: (() => void) | null = null
let _refreshTimer: ReturnType<typeof setTimeout> | null = null

export function setToken(token: string | null): void {
  _token = token
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token)
    scheduleTokenRefresh(token)
  } else {
    sessionStorage.removeItem(TOKEN_KEY)
    if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null }
  }
}

export function getToken(): string | null {
  return _token
}

export function setOnUnauthorized(fn: () => void): void {
  _onUnauthorized = fn
}

interface TokenPayload {
  exp?: number
  [key: string]: unknown
}

/** Decode JWT payload to get expiry time */
function decodeTokenExp(token: string): number | null {
  try {
    const payload: TokenPayload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? payload.exp * 1000 : null
  } catch { return null }
}

/** Schedule auto-refresh 5 minutes before token expiry */
function scheduleTokenRefresh(token: string): void {
  if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null }
  const exp = decodeTokenExp(token)
  if (!exp) return
  const refreshIn = Math.max(0, exp - Date.now() - 5 * 60 * 1000) // 5 min before expiry
  if (refreshIn <= 0) return // already expired or about to
  _refreshTimer = setTimeout(async () => {
    try {
      const res = await request<{ access_token: string }>('/auth/refresh', { method: 'POST' })
      if (res) setToken(res.access_token)
    } catch {
      // Refresh failed, token will expire naturally and 401 handler will trigger
    }
  }, refreshIn)
}

// Schedule refresh for existing token on module load
if (_token) scheduleTokenRefresh(_token)

interface RequestInit {
  method?: string
  headers?: Record<string, string>
  body?: BodyInit | null
  // /api/health 在依赖降级时按监控惯例回 503，但负载里写着是哪一项挂了；
  // 默认抛错会让前端只看到一个 "HTTP 503"，说不出问题在哪
  allow503?: boolean
  // 2FA 待验证阶段手里只有 login 发的受限临时令牌（scope=2fa_pending），
  // 会话里还没有正式 token。这时必须显式指定用哪枚令牌，不能沿用 _token。
  token?: string
}

interface ApiErrorDetail {
  msg?: string
  message?: string
}

/** 套餐门控（402）与冷层取回（409）这类结构化错误：detail 不是字符串而是对象 */
interface ApiErrorObjectDetail extends ApiErrorDetail {
  error?: string
  current_plan?: string
  suggest_plan?: string
  upgrade_url?: string
  restore_accepted?: boolean
  retry_after_seconds?: number
}

interface ApiErrorResponse {
  detail?: string | ApiErrorDetail[] | ApiErrorObjectDetail
  message?: string
  retry_after?: number
}

export class ApiError extends Error {
  status: number
  upgradeUrl?: string
  suggestPlan?: string
  retryAfter?: number
  /** 文件在冷存储层：archived=还没提交取回，restoring=正在取回 */
  coldState?: 'archived' | 'restoring'
  restoreAccepted?: boolean

  constructor(message: string, status: number, extra: Partial<ApiError> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.upgradeUrl = extra.upgradeUrl
    this.suggestPlan = extra.suggestPlan
    this.retryAfter = extra.retryAfter
    this.coldState = extra.coldState
    this.restoreAccepted = extra.restoreAccepted
  }
}

/**
 * 把后端错误响应翻成用户看得懂的一句话。
 *
 * 三种 detail 形状都要认：字符串（多数端点）、FastAPI 校验数组、
 * 以及 402 套餐门控用的对象（message/suggest_plan/upgrade_url）。
 * 对象那一路以前不被识别，免费客户点"批量注入""导出报告"只看到一行
 * "HTTP 402" —— 等于明明告诉他有这功能，却不给解锁入口。
 */
async function buildApiError(response: Response): Promise<ApiError> {
  let errorMessage = `HTTP ${response.status}`
  const extra: Partial<ApiError> = { status: response.status }
  try {
    const errorData: ApiErrorResponse = await response.json()
    const detail = errorData.detail
    if (Array.isArray(detail)) {
      errorMessage = detail.map(e => e.msg || e.message || '').filter(Boolean).join('; ') || '参数校验失败'
    } else if (typeof detail === 'string') {
      errorMessage = detail
    } else if (detail && typeof detail === 'object') {
      if (typeof detail.message === 'string') errorMessage = detail.message
      extra.upgradeUrl = detail.upgrade_url
      extra.suggestPlan = detail.suggest_plan
      // 冷层取回：409 的 message 里已经带了"要等多久"，直接给用户看那句话，
      // 别再把它演成"暂无数据"或一行 HTTP 409
      if (detail.error === 'archived' || detail.error === 'restoring') {
        extra.coldState = detail.error
        extra.restoreAccepted = detail.restore_accepted !== false
        if (typeof detail.retry_after_seconds === 'number') {
          extra.retryAfter = detail.retry_after_seconds
        }
      }
    } else if (errorData.message) {
      errorMessage = errorData.message
    }
    if (response.status === 429 && typeof errorData.retry_after === 'number') {
      extra.retryAfter = errorData.retry_after
      errorMessage = `${errorMessage}（约 ${errorData.retry_after} 秒后可重试）`
    }
  } catch { /* 响应体不是 JSON 时保留 HTTP 状态码兜底 */ }
  return new ApiError(errorMessage, response.status, extra)
}

async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers || {}),
  }

  if (_token) {
    headers['Authorization'] = `Bearer ${_token}`
  }
  // 显式传入的令牌优先（2FA 临时令牌场景）
  if (options.token) {
    headers['Authorization'] = `Bearer ${options.token}`
  }

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData) && options.body) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(path.startsWith('/api/') ? path : `${BASE_URL}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 503 && (options as RequestInit).allow503) {
    // 降级负载原样交回调用方：只抛 "HTTP 503" 的话，界面说不出是哪一项挂了
    return (await response.json()) as T
  }

  if (response.status === 401) {
    _token = null
    if (_onUnauthorized) {
      _onUnauthorized()
    }
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    throw await buildApiError(response)
  }

  if (response.status === 204) return null as T
  return response.json()
}

export function get<T = unknown>(path: string): Promise<T> {
  return request<T>(path)
}

export function post<T = unknown>(path: string, data?: unknown, options: RequestInit = {}): Promise<T> {
  return request<T>(path, {
    ...options,
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data),
  })
}

export function put<T = unknown>(path: string, data: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export function patch<T = unknown>(path: string, data: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export function del<T = unknown>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' })
}

export function upload<T = unknown>(path: string, formData: FormData): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: formData,
  })
}

/**
 * Download a file from the API (handles blob responses, not JSON).
 * Triggers a browser download via a temporary anchor element.
 */
export async function download(path: string, options: RequestInit = {}): Promise<void> {
  const headers: Record<string, string> = { ...(options.headers || {}) }
  if (_token) {
    headers['Authorization'] = `Bearer ${_token}`
  }
  if (!(options.body instanceof FormData) && options.body) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    _token = null
    if (_onUnauthorized) _onUnauthorized()
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    throw await buildApiError(response)
  }

  // Extract filename from Content-Disposition header
  const disposition = response.headers.get('Content-Disposition') || ''
  let filename = 'download'
  const match = disposition.match(/filename="?([^";]+)"?/)
  if (match) filename = decodeURIComponent(match[1])

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

interface GetAssetsParams {
  page?: number
  page_size?: number
  file_type?: string
  search?: string
  sort_by?: string
  folder_id?: string
}

interface SearchOptions {
  page_size?: number
  rerank?: boolean
  file_type?: string
}

interface SimilarSearchParams {
  file?: File
  asset_id?: string
  top_k?: number
  query_text?: string
}

interface ShareOptions {
  // 后端字段就叫 expire_hours：此前发的是 expires_hours，被静默忽略，
  // 请求 1 小时实得默认 7 天。max_views 后端没有实现，先从契约里拿掉别当承诺。
  expire_hours?: number
  password?: string
}

interface GetBrandProfileParams {
  fact_type?: string
  established_only?: boolean
}

interface OnboardingData {
  brand_name?: string
  industry?: string
  [key: string]: unknown
}

interface InvitationCodeData {
  code?: string
  max_uses?: number
  [key: string]: unknown
}

// ── 品牌合规 ──
export interface ComplianceViolation {
  word: string
  tier: number
  tier_label: string
  context: string
  position: number
  suggestion: string
}

export interface ComplianceColorMatch {
  brand_color: string
  matched_color: string | null
  score: number
  delta_e: number | null
  level: string
}

export interface ComplianceRuleResult {
  rule: string
  type: string
  field: string
  description: string
  passed: boolean
  reason: string
}

export interface ComplianceResult {
  score: number
  issues: string[]
  passed: boolean
  capped_by_tier1?: boolean
  breakdown: Record<string, Record<string, unknown>>
  violations: ComplianceViolation[]
  color_matches: ComplianceColorMatch[]
  rule_results: ComplianceRuleResult[]
  suggestions: string[]
  checked_at?: string
}

export interface ComplianceCheckPayload {
  asset_id?: string
  text?: string
  colors?: string[]
  brand_colors?: string[]
  industry?: string
}

export interface ComplianceRulesResponse {
  industry: string
  industry_name: string
  rules: Array<Record<string, unknown>>
  weights: Record<string, number>
  tier1_score_cap: number
}

export interface ComplianceReportResponse {
  asset_id: string
  original_name: string
  source: 'check' | 'pipeline'
  report: ComplianceResult
}

export interface ComplianceStats {
  total_assets: number
  passed: number
  needs_review: number
  violated: number
  unchecked: number
  avg_score: number | null
  coverage: number
}

// ── Knowledge graph types ──
export interface GraphNode {
  id: string
  name: string
  entity_type?: string
  type?: string
  aliases?: string[]
  description?: string
  confidence?: number
  created_at?: string | null
}

export interface GraphEdge {
  id?: string
  source?: string
  target?: string
  source_entity_id?: string
  target_entity_id?: string
  relation_type: string
  confidence?: number
  evidence?: string
}

export interface KnowledgeGraphData {
  nodes: GraphNode[]
  edges: GraphEdge[]
  depth?: number
  truncated?: boolean
}

export interface EntityListResult {
  total: number
  entities: GraphNode[]
}

export interface EntityRelationDict {
  id?: string
  source_entity_id?: string
  target_entity_id?: string
  relation_type: string
  confidence?: number
  evidence?: string
}

export interface EntityDetail {
  id: string
  name: string
  entity_type: string
  aliases?: string[]
  description?: string
  confidence?: number
  created_at?: string | null
  metadata?: Record<string, unknown>
  relations?: { outgoing: EntityRelationDict[]; incoming: EntityRelationDict[] }
  facts?: {
    entity_id?: string
    entity_name?: string
    total: number
    asset_count?: number
    by_asset?: Array<{ asset_id: string; fact_count?: number; facts: Array<Record<string, unknown>> }>
  }
}

interface GetEntitiesParams {
  name?: string
  entity_type?: string
  limit?: number
}

// Convenience methods for common endpoints
export interface ReviewQueueAsset {
  asset_id: string
  original_name: string
  file_type: string
  status: string
  ai_confidence: number
  model_used: string
  reviewed_at: string | null
  updated_at: string | null
  fact_count: number
}

export interface ReviewQueueFact {
  fact_id: string
  asset_id: string
  original_name: string
  fact_type: string
  fact_value: string
  source: string
  trust_weight: number
  reinforcement_count: number
  evidence: string
}

export interface ReviewQueue {
  assets_pending: ReviewQueueAsset[]
  facts_promotable: ReviewQueueFact[]
  counts: {
    assets_pending: number
    facts_promotable: number
    assets_never_injected: number
  }
}

export const api = {
  // Assets
  getAssets: (params: GetAssetsParams = {}) => {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.page_size) query.set('page_size', String(params.page_size))
    if (params.file_type) query.set('file_type', params.file_type)
    if (params.search) query.set('search', params.search)
    if (params.sort_by) query.set('sort_by', params.sort_by)
    if (params.folder_id) query.set('folder_id', params.folder_id)
    const qs = query.toString()
    return get<{ items: Asset[]; total: number; page: number; page_size: number }>(`/assets${qs ? `?${qs}` : ''}`)
  },
  getAsset: (id: string) => get<Asset>(`/assets/${id}`),
  getAssetStats: () => get<AssetStats>('/assets/stats'),
  uploadAsset: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return upload<Asset>('/assets', formData)
  },
  checkDedup: async (file: File) => {
    const md5_hash = await computeFileMD5(file)
    return post<DedupCheckResponse>('/assets/check-dedup', { md5_hash, filename: file.name })
  },
  deleteAsset: (id: string) => del(`/assets/${id}`),
  infuseAsset: (id: string) => post<{ task_id: string }>(`/assets/${id}/infuse`),
  /** 把冷层的文件取回成可读；返回里 state=restoring 表示已提交、readable 表示现在就能读 */
  restoreAsset: (id: string, urgent = false) =>
    post<{
      asset_id: string
      state: 'restoring' | 'readable' | 'failed'
      already_readable?: boolean
      detail?: string
      retry_after_seconds?: number | null
    }>(`/assets/${id}/restore?urgent=${urgent}`),
  infuseAll: () => post<{ task_id: string }>('/assets/infuse-all'),

  // Two-phase direct upload
  getUploadUrl: (filename: string, fileSize: number, contentType?: string, md5Hash?: string) =>
    post<UploadUrlResponse>('/assets/upload-url', {
      filename, file_size: fileSize,
      content_type: contentType || 'application/octet-stream',
      md5_hash: md5Hash || '',
    }),
  // 后端这里回的是 {asset_id,status,message}，不是 Asset —— 类型写错会让调用方
  // 以为能直接读到 id/字段（现在没人读，但按类型补代码就会拿到 undefined）
  completeUpload: (assetId: string, md5Hash?: string, etag?: string) =>
    post<{ asset_id: string; status: string; message?: string }>(`/assets/${assetId}/upload-complete`, {
      md5_hash: md5Hash || '', etag: etag || '',
    }),
  directUpload: async (uploadUrl: string, file: File, headers: Record<string, string> = {}) => {
    const resp = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', ...headers },
    })
    if (!resp.ok) throw new Error(`直传失败: ${resp.status}`)
    return { etag: resp.headers.get('ETag') || '' }
  },
  getStorageInfo: () => get<StorageInfo>('/assets/storage-info'),
  getQuota: () => get<StorageQuota>('/assets/quota'),

  // Search
  search: (query: string, options: SearchOptions = {}) => post<SearchResponse>('/search', {
    query,
    page_size: options.page_size || 50,
    rerank: options.rerank ?? true,
    file_type: options.file_type,
  }),
  searchSuggest: (q: string) => get<{ suggestions: string[] }>(`/search/suggest?q=${encodeURIComponent(q)}`),

  // 以图搜图 / 文搜图
  similarSearch: (params: SimilarSearchParams = {}) => {
    const formData = new FormData()
    if (params.file) {
      formData.append('file', params.file)
    } else if (params.asset_id) {
      formData.append('asset_id', params.asset_id)
    } else if (params.query_text) {
      formData.append('query_text', params.query_text)
    }
    if (params.top_k) {
      formData.append('top_k', String(params.top_k))
    }
    return upload<SimilarSearchResult[]>('/similar', formData)
  },

  // Tags
  getTags: () => get<Tag[]>('/tags'),

  // BMI
  getBMI: () => get<BMIResult>('/bmi'),

  // Knowledge
  getKnowledgeFacts: (assetId: string) => get<KnowledgeFacts>(`/knowledge/${assetId}/facts`),
  // AI 结果审阅：待审队列 + 人工判定（确认/驳回/晋升/纠错）
  getReviewQueue: (limit = 50) => get<ReviewQueue>(`/knowledge/review-queue?limit=${limit}`),
  reviewAsset: (assetId: string, action: 'approve' | 'reject', note = '') =>
    post<{ asset_id: string; status: string; status_before: string }>(
      `/assets/${assetId}/ai-review`, { action, note }),
  confirmFact: (factId: string) =>
    post<{ promoted: boolean }>(`/knowledge/facts/${factId}/confirm`, {}),
  correctFact: (factId: string, factValue: string, metadata?: Record<string, unknown>) =>
    patch<{ corrected: boolean }>(`/knowledge/facts/${factId}`,
      { fact_value: factValue, ...(metadata ? { metadata } : {}) }),

  // Compliance 品牌合规
  getComplianceRules: (industry?: string) =>
    get<ComplianceRulesResponse>(`/compliance/rules${industry ? `?industry=${encodeURIComponent(industry)}` : ''}`),
  checkCompliance: (payload: ComplianceCheckPayload) =>
    post<ComplianceResult & { asset_id?: string | null }>('/compliance/check', payload),
  getComplianceReport: (assetId: string) =>
    get<ComplianceReportResponse>(`/compliance/report/${assetId}`),
  getComplianceStats: () => get<ComplianceStats>('/compliance/stats'),
  getKnowledgeGraph: (depth?: number) =>
    get<KnowledgeGraphData>(`/knowledge/graph${depth ? `?depth=${depth}` : ''}`),
  getEntities: (params: GetEntitiesParams = {}) => {
    const q = new URLSearchParams()
    if (params.name) q.set('name', params.name)
    if (params.entity_type) q.set('entity_type', params.entity_type)
    if (params.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return get<EntityListResult>(`/knowledge/entities${qs ? `?${qs}` : ''}`)
  },
  getEntityDetail: (id: string) => get<EntityDetail>(`/knowledge/entities/${id}`),
  extractEntities: () => post<Record<string, unknown>>('/knowledge/extract-entities'),

  // Vault
  getVaultStats: () => get<VaultStats>('/vault/stats'),
  getVaultInfo: (assetId: string) => get<VaultItem>(`/vault/${assetId}`),
  verifyVault: (assetId: string) => post<VaultItem>(`/vault/${assetId}/verify`),
  // 后端是同步返回计数的（vault_service.batch_verify → {total,passed,failed,skipped,results}），
  // 这里以前标成 {task_id}，于是调用方去读 data.verified，永远拿到 undefined → 恒显示 0 通过
  // skipped：对象在冷层未取回，后端跳过而不误判成篡改，页面必须把它跟"不一致"分开说
  batchVerifyAll: () => post<{ total: number; passed: number; failed: number; skipped?: number; results: unknown[] }>('/vault/verify-all'),
  archiveAsset: (assetId: string, reason = '') => post<VaultItem>(`/vault/${assetId}/archive`, { reason }),
  unarchiveAsset: (assetId: string) => post<VaultItem>(`/vault/${assetId}/unarchive`),

  // Auth
  login: (email: string, password: string) => post<{ access_token: string; user: User }>('/auth/login', { email, password }),
  register: (email: string, password: string, name: string) => post<{ access_token: string; user: User }>('/auth/register', { email, password, name }),
  // 退出必须让后端吊销令牌：只清本地 storage 的话，那枚 JWT 在
  // jwt_expire_minutes（当前 30 分钟）内依然可用，共用电脑或令牌外泄时"退出"是假的，
  // 而且后端 user_logout 审计事件永远不会产生。
  logout: () => post<{ message: string }>('/auth/logout', {}),
  getMe: () => get<User>('/auth/me'),
  refreshToken: () => post<{ access_token: string }>('/auth/refresh'),

  // SMS
  sendSmsCode: (phone: string) => post<{ message: string }>('/auth/sms/send', { phone }),

  // Phone auth
  phoneRegister: (phone: string, code: string, password: string, name: string) =>
    post<{ access_token: string; user: User }>('/auth/phone/register', { phone, code, password, name }),
  phoneLogin: (phone: string, code: string) =>
    post<{ access_token: string; user: User }>('/auth/phone/login', { phone, code }),

  // Password reset
  resetPassword: (phone: string, code: string, newPassword: string) =>
    post<{ message: string }>('/auth/reset-password', { phone, code, new_password: newPassword }),

  // WeChat
  getWechatQr: () => get<{ qr_url: string; state: string }>('/auth/wechat/qr'),

  // Tasks
  getTask: (taskId: string) => get<Task>(`/tasks/${taskId}`),

  // Shares
  createShare: (assetId: string, options: ShareOptions = {}) =>
    post<ShareLink>('/shares', { asset_id: assetId, ...options }),
  listShares: (page = 1) => get<{ items: ShareLink[]; total: number }>(`/shares?page=${page}`),
  revokeShare: (shareId: string) => del(`/shares/${shareId}`),

  // Reports
  // format 在后端是 Query 参数（reports.py），放 body 会被忽略而静默走默认值
  exportReport: (format: 'html' = 'html') => download(`/reports/brand?format=${format}`, {
    method: 'POST',
  }),

  // Brand profile
  getBrandProfile: (params: GetBrandProfileParams = {}) => {
    const q = new URLSearchParams()
    if (params.fact_type) q.set('fact_type', params.fact_type)
    if (params.established_only) q.set('established_only', 'true')
    const qs = q.toString()
    return get<BrandProfile>(`/brand-profile${qs ? `?${qs}` : ''}`)
  },
  getBrandProfileStats: () => get<BrandProfileStats>('/brand-profile/stats'),

  // Team
  getTeamMembers: () => get<TeamMember[]>('/team/members'),
  inviteMember: (email: string, name: string, role: string) =>
    post<{ message: string }>('/team/invite', { email, name, role }),
  acceptInvite: (token: string, password: string, name: string) =>
    post<{ access_token: string; user: User }>('/team/accept-invite', { token, password, name }),
  changeMemberRole: (userId: string, role: string) =>
    put<{ message: string }>(`/team/members/${userId}/role`, { role }),
  updateMemberRole: (userId: string, role: string) =>
    put<{ message: string }>(`/team/members/${userId}/role`, { role }),
  removeMember: (userId: string) => del(`/team/members/${userId}`),
  getTeamUsage: () => get<TeamUsage>('/team/usage'),
  upgradeToEnterprise: (data: { company_name?: string; industry?: string } = {}) =>
    post<{ message: string }>('/team/upgrade-to-enterprise', data),

  // Search click tracking (for BMI quality metrics)
  trackSearchClick: (query: string, assetId: string, position: number) =>
    post<{ message: string }>('/search/click', {
      query, asset_id: assetId, position,
    }),

  // Security / 2FA
  get2FAStatus: () => get<TwoFAStatus>('/security/2fa/status'),
  setup2FA: () => post<TwoFASetup>('/security/2fa/setup'),
  verify2FA: (code: string) => post<{ message: string }>('/security/2fa/verify', { code }),
  disable2FA: (code: string) => post<{ message: string }>('/security/2fa/disable', { code }),
  // 2FA 登录续验只能打 /auth/verify-2fa：它认 login 发的 2fa_pending 临时令牌，
  // 并回一枚完整 JWT。/security/2fa/verify-login 要的是 get_current_user
  // （完整令牌），此刻手里还没有 —— 拿它续验必然 401，且它只回 {verified}，不发会话。
  verify2FALogin: (tempToken: string, identifier: string, code: string) =>
    post<{ access_token: string; user: User }>('/auth/verify-2fa',
      { email: identifier, totp_code: code }, { token: tempToken }),

  // Data export / deletion (GDPR)
  // 回包形状来自 app/api/v1/security.py 的 DataExportResponse；
  // 以前的 {message, filename?} 是幻影：status/download_url 才是页面判断成败的依据
  requestDataExport: () => post<{ request_id: string; status: string; download_url?: string;
    expires_at?: string; encrypted?: boolean; zip_password?: string; message: string }>(
    '/security/data/export'),
  downloadDataExport: (filename: string) => download(`/security/data/download/${filename}`),
  requestDataDeletion: (requestType: string, reason?: string, confirm?: boolean) =>
    post<{ message: string }>('/security/data/delete', {
      request_type: requestType, reason: reason || '', confirm: !!confirm,
    }),

  // Audit logs
  getAuditLogs: (page = 1, eventType = '') => {
    const q = new URLSearchParams()
    q.set('page', String(page))
    if (eventType) q.set('event_type', eventType)
    return get<{ items: AuditLog[]; total: number; page: number }>(`/security/audit-logs?${q.toString()}`)
  },

  // Change password
  changePassword: (oldPassword: string, newPassword: string) =>
    post<{ message: string }>('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),

  // Onboarding
  getOnboardingStatus: () => get<OnboardingStatus>('/onboarding/status'),
  completeOnboarding: (data: OnboardingData) => post<{ message: string }>('/onboarding/complete', data),
  validateInvitationCode: (code: string) => post<{ valid: boolean; plan?: string }>('/invitation/validate', { code }),
  getMyInvitationCodes: () => get<InvitationCode[]>('/invitation/my-codes'),
  createInvitationCode: (data: InvitationCodeData) => post<InvitationCode>('/invitation/create', data),
}

// Storage management endpoints
export const storageApi = {
  getTiers: () => get<StorageTierStats>('/storage/tiers'),
  getDedup: () => get<DedupReport>('/storage/dedup'),
  getSavings: () => get<SavingsReport>('/storage/savings'),
  refreshTiers: () => post<{ message: string }>('/storage/refresh-tiers'),
}

// Plans / subscription endpoints
export const plansApi = {
  listPlans: () => get<PlansResponse>('/plans'),
  getCurrent: () => get<CurrentPlan>('/plans/current'),
  upgrade: (targetPlan: string) => post<{
    status: string
    message: string
    current_plan: string
    target_plan: string
    target_price_monthly: number
    target_price_yearly: number
  }>('/plans/upgrade', { target_plan: targetPlan }),
}

// GET /api/health — app/main.py health_check()，全局版本与生效存储适配器的唯一事实源
export interface HealthStatus {
  status: 'ok' | 'degraded'
  version: string
  storage: string
  timestamp: string
  checks: Record<string, 'up' | 'down'>
}

export const healthApi = {
  getHealth: () => request<HealthStatus>('/api/health', { allow503: true }),
}
